import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, AlertCircle, CheckCircle } from 'lucide-react';

const SelectKeyDialog = ({ isOpen, onClose, onSelectKey, setupId }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      fetchKeys();
    }
  }, [isOpen]);

  const fetchKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/keys-with-status`, {
        withCredentials: true
      });
      setKeys(response.data.keys || []);
    } catch (err) {
      console.error('Error fetching keys:', err);
      setError('Ошибка загрузки ключей');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectKey = (key) => {
    if (key.is_active) {
      return; // Don't allow selecting active keys
    }
    onSelectKey(key);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Выберите ключ для запуска</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ff88]"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-400">
              <AlertCircle className="h-6 w-6 mr-2" />
              {error}
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 mb-4">У вас нет подключенных ключей</p>
              <a
                href="/exchanges"
                className="inline-block px-4 py-2 bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black rounded-lg hover:opacity-90 transition-opacity"
              >
                Подключить биржу
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              {keys.map((key) => {
                const isAvailable = !key.is_active;
                const balance = key.balance;
                const usdtBalance = balance?.usdt?.total || 0;
                const usdcBalance = balance?.usdc?.total || 0;
                const totalBalance = usdtBalance + usdcBalance;

                return (
                  <div
                    key={key.id}
                    onClick={() => handleSelectKey(key)}
                    className={`
                      p-4 rounded-lg border transition-all cursor-pointer
                      ${isAvailable 
                        ? 'border-gray-700 hover:border-[#00ff88] hover:bg-[#00ff88]/5' 
                        : 'border-gray-800 bg-gray-900/50 cursor-not-allowed opacity-60'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Exchange and Key Name */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-semibold">
                            {key.exchange_name === 'binance' ? 'Binance' : 
                             key.exchange_name === 'bybit' ? 'ByBit' : 
                             key.exchange_name.toUpperCase()}
                          </span>
                          <span className="text-gray-400">:</span>
                          <span className="text-[#00ff88]">{key.key_name}</span>
                        </div>

                        {/* Balance */}
                        <div className="flex items-center gap-4 text-sm mb-2">
                          <div>
                            <span className="text-gray-400">Баланс: </span>
                            <span className="text-white font-mono">
                              ${totalBalance.toFixed(2)}
                            </span>
                          </div>
                          {usdtBalance > 0 && (
                            <div className="text-gray-400">
                              USDT: <span className="text-gray-300">${usdtBalance.toFixed(2)}</span>
                            </div>
                          )}
                          {usdcBalance > 0 && (
                            <div className="text-gray-400">
                              USDC: <span className="text-gray-300">${usdcBalance.toFixed(2)}</span>
                            </div>
                          )}
                        </div>

                        {/* Status */}
                        {key.is_active && (
                          <div className="flex items-center gap-2 text-sm text-amber-500">
                            <AlertCircle className="h-4 w-4" />
                            <span>
                              Используется в сетапе: <span className="font-semibold">{key.active_setup_title}</span>
                            </span>
                          </div>
                        )}
                        
                        {key.error && (
                          <div className="flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="h-4 w-4" />
                            <span>{key.error}</span>
                          </div>
                        )}

                        {/* Testnet Badge */}
                        {key.testnet && (
                          <div className="inline-block mt-2 px-2 py-1 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
                            Testnet
                          </div>
                        )}
                      </div>

                      {/* Status Icon */}
                      <div className="ml-4">
                        {isAvailable ? (
                          <div className="w-6 h-6 rounded-full border-2 border-[#00ff88] flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-[#00ff88]" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center">
                            <AlertCircle className="h-4 w-4 text-gray-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectKeyDialog;
